import { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/dialog'
import {
  ArrowLeft,
  Mail,
  Calendar,
  MessageSquare,
  Building2,
  Shield,
  ShieldOff,
  UserX,
  UserCheck as UserReactivate,
  Cpu,
  Coins,
} from 'lucide-react'
import { API_BASE_URL, SUPER_ADMIN_EMAIL } from '../../../lib/config'
import { formatISTDate } from '../../../components/lib/dateUtils'
import { useAuth } from '../../../components/auth/AuthProvider'
import { UserSessionsModal } from './UserSessionsModal'
import { AdminPanelButton } from './AdminPanelButton'

interface UserDetailData {
  id: string
  username: string
  email: string
  firstName?: string
  lastName?: string
  role?: string
  isActive?: boolean
  createdAt: string
  chatCount: number
  messageCount: number
  lastActive?: string
  preferredChatLlmProvider?: string | null
  effectiveChatLlmProvider?: string
  globalChatLlmProvider?: string
  /** explorer | builder | pioneer (from server; null user row → default tier) */
  creditTier?: string
  credit?: UserCreditInfo
}

interface UserCreditInfo {
  creditTier?: string
  creditTierLabel?: string
  overrideMonthlyMessageLimit?: number | null
  overrideMonthlyCostUsdLimit?: number | null
  tierDefaultMonthlyMessageLimit?: number | null
  tierDefaultMonthlyCostUsdLimit?: number | null
  monthlyMessageLimit?: number | null
  monthlyCostUsdLimit?: number | null
  monthlyUserMessagesUsed?: number
  monthlyCostUsd?: number
  creditLimitExceeded?: boolean
  messageQuotaExceeded?: boolean
  costQuotaExceeded?: boolean
  billingPeriodStartIst?: string
}

interface BusinessInfo {
  name?: string
  about?: string
  businessType?: string
  natureOfBusiness?: string
  industry?: string
  location?: string
  subindustry?: string
}

/** Matches GET /admin/user/{user_id}/sessions (with usage aggregates) */
interface AdminUserSession {
  id: string
  name: string
  created_at: string | null
  message_count: number
  deleted_count: number
  is_deleted?: string
  deleted_at?: string | null
  deleted_by_user?: string | null
  total_input_tokens: number
  total_output_tokens: number
  total_cost: number
}

const SESSIONS_USAGE_PAGE_SIZE = 30

interface SessionsPagination {
  total: number
  hasMore: boolean
  nextOffset: number | null
}

interface SessionsOverallUsage {
  totalInput: number
  totalOutput: number
  totalCost: number
}

const CREDIT_TIER_OPTIONS: { id: string; label: string }[] = [
  { id: 'explorer', label: 'Explorer' },
  { id: 'builder', label: 'Builder' },
  { id: 'pioneer', label: 'Pioneer' },
]

const emptyBusinessInfo: BusinessInfo = {
  name: '',
  about: '',
  businessType: '',
  natureOfBusiness: '',
  industry: '',
  location: '',
  subindustry: '',
}

export function UserDetailPage({ userId, onBack }: { userId: string; onBack: () => void }) {
  const { user: currentUser, isAdmin } = useAuth()
  const isSuperAdmin = currentUser?.email === SUPER_ADMIN_EMAIL
  const [user, setUser] = useState<UserDetailData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [businessDialogOpen, setBusinessDialogOpen] = useState(false)
  const [businessForm, setBusinessForm] = useState<BusinessInfo>(emptyBusinessInfo)
  const [businessSaving, setBusinessSaving] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  const [sessions, setSessions] = useState<AdminUserSession[]>([])
  const [sessionsLoading, setSessionsLoading] = useState(false)
  const [sessionsLoadingMore, setSessionsLoadingMore] = useState(false)
  const [sessionsPagination, setSessionsPagination] = useState<SessionsPagination>({
    total: 0,
    hasMore: false,
    nextOffset: null,
  })
  const [sessionsOverallUsage, setSessionsOverallUsage] = useState<SessionsOverallUsage>({
    totalInput: 0,
    totalOutput: 0,
    totalCost: 0,
  })
  const [chatHistoryOpen, setChatHistoryOpen] = useState(false)
  const [chatHistoryInitialSessionId, setChatHistoryInitialSessionId] = useState<string | null>(null)
  const [singleSessionModal, setSingleSessionModal] = useState<{
    open: boolean
    id: string | null
    name: string
    createdAt: string | null
  }>({
    open: false,
    id: null,
    name: '',
    createdAt: null,
  })
  const [llmOptions, setLlmOptions] = useState<{ id: string; label: string }[]>([])
  const [llmSelect, setLlmSelect] = useState('')
  const [llmSaving, setLlmSaving] = useState(false)
  const [llmError, setLlmError] = useState<string | null>(null)
  const [creditTierSelect, setCreditTierSelect] = useState('explorer')
  const [creditMsgInput, setCreditMsgInput] = useState('')
  const [creditUsdInput, setCreditUsdInput] = useState('')
  const [creditSaving, setCreditSaving] = useState(false)
  const [creditError, setCreditError] = useState<string | null>(null)

  const fetchUser = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch(`${API_BASE_URL}admin/users/${userId}`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setUser(data)
      } else {
        setError('User not found')
        setUser(null)
      }
    } catch {
      setError('Failed to load user')
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    fetchUser()
  }, [fetchUser])

  useEffect(() => {
    let cancelled = false
    fetch(`${API_BASE_URL}admin/configurations/llm-provider`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.options || !Array.isArray(data.options)) return
        setLlmOptions(data.options as { id: string; label: string }[])
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (user) {
      setLlmSelect(user.preferredChatLlmProvider ?? '')
      setLlmError(null)
      const c = user.credit
      const tier =
        c?.creditTier && CREDIT_TIER_OPTIONS.some((o) => o.id === c.creditTier)
          ? c.creditTier
          : user.creditTier && CREDIT_TIER_OPTIONS.some((o) => o.id === user.creditTier)
            ? user.creditTier
            : 'explorer'
      setCreditTierSelect(tier)
      setCreditMsgInput(
        c?.overrideMonthlyMessageLimit != null ? String(c.overrideMonthlyMessageLimit) : ''
      )
      setCreditUsdInput(
        c?.overrideMonthlyCostUsdLimit != null ? String(c.overrideMonthlyCostUsdLimit) : ''
      )
      setCreditError(null)
    }
  }, [user])

  const fetchSessionsPage = useCallback(
    async (offset = 0, append = false) => {
      if (!append) setSessionsLoading(true)
      else setSessionsLoadingMore(true)
      try {
        const qs = new URLSearchParams({
          limit: String(SESSIONS_USAGE_PAGE_SIZE),
          offset: String(offset),
        })
        const res = await fetch(`${API_BASE_URL}admin/user/${userId}/sessions?${qs}`, {
          credentials: 'include',
        })
        if (!res.ok) {
          if (!append) setSessions([])
          return
        }
        const data = await res.json()
        const list: AdminUserSession[] = Array.isArray(data?.sessions) ? data.sessions : []
        setSessions((prev) =>
          append ? [...prev, ...list.filter((s) => !prev.some((p) => p.id === s.id))] : list
        )
        setSessionsPagination({
          total: typeof data?.total === 'number' ? data.total : list.length,
          hasMore: Boolean(data?.has_more),
          nextOffset: data?.next_offset ?? null,
        })
        setSessionsOverallUsage({
          totalInput: Number(data?.overall_total_input_tokens ?? 0),
          totalOutput: Number(data?.overall_total_output_tokens ?? 0),
          totalCost: Number(data?.overall_total_cost ?? 0),
        })
      } catch {
        if (!append) setSessions([])
      } finally {
        if (!append) setSessionsLoading(false)
        else setSessionsLoadingMore(false)
      }
    },
    [userId]
  )

  useEffect(() => {
    if (!userId) return
    void fetchSessionsPage(0, false)
  }, [userId, fetchSessionsPage])

  const loadMoreSessions = useCallback(() => {
    if (!sessionsPagination.hasMore || sessionsPagination.nextOffset == null || sessionsLoadingMore) return
    void fetchSessionsPage(sessionsPagination.nextOffset, true)
  }, [sessionsPagination, sessionsLoadingMore, fetchSessionsPage])

  const openChatHistory = (sessionId?: string) => {
    setChatHistoryInitialSessionId(sessionId ?? null)
    setChatHistoryOpen(true)
  }

  const openSingleSessionMessages = (session: AdminUserSession) => {
    setSingleSessionModal({
      open: true,
      id: session.id,
      name: session.name || 'Session',
      createdAt: session.created_at ?? null,
    })
  }

  const openBusinessDialog = async () => {
    if (!user) return
    setBusinessForm(emptyBusinessInfo)
    try {
      const res = await fetch(`${API_BASE_URL}admin/user/${user.id}/business-info`, { credentials: 'include' })
      if (res.ok) {
        const data = await res.json()
        setBusinessForm({
          name: data.name ?? '',
          about: data.about ?? '',
          businessType: data.businessType ?? '',
          natureOfBusiness: data.natureOfBusiness ?? '',
          industry: data.industry ?? '',
          location: data.location ?? '',
          subindustry: data.subindustry ?? '',
        })
      }
    } catch (e) {
      console.error(e)
    }
    setBusinessDialogOpen(true)
  }

  const saveBusinessInfo = async () => {
    if (!user || !businessForm.name?.trim()) {
      alert('Business name is required.')
      return
    }
    setBusinessSaving(true)
    try {
      const res = await fetch(`${API_BASE_URL}admin/user/${user.id}/business-info`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(businessForm),
      })
      if (res.ok) {
        setBusinessDialogOpen(false)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setBusinessSaving(false)
    }
  }

  const promoteToAdmin = async () => {
    if (!user || !isSuperAdmin || user.email === SUPER_ADMIN_EMAIL) return
    setActionLoading('promote')
    try {
      const res = await fetch(`${API_BASE_URL}admin/users/${user.id}/promote`, { method: 'POST', credentials: 'include' })
      const data = await res.json().catch(() => ({}))
      if (res.ok) await fetchUser()
      else alert(data?.error || 'Failed to promote')
    } finally {
      setActionLoading(null)
    }
  }

  const demoteToUser = async () => {
    if (!user || !isSuperAdmin || user.email === SUPER_ADMIN_EMAIL) return
    setActionLoading('demote')
    try {
      const res = await fetch(`${API_BASE_URL}admin/users/${user.id}/demote`, { method: 'POST', credentials: 'include' })
      const data = await res.json().catch(() => ({}))
      if (res.ok) await fetchUser()
      else alert(data?.error || 'Failed to demote')
    } finally {
      setActionLoading(null)
    }
  }

  const deactivateUser = async () => {
    if (!user || !isAdmin || user.email === SUPER_ADMIN_EMAIL) return
    if (!confirm(`Deactivate ${user.username}? They will not be able to log in.`)) return
    setActionLoading('deactivate')
    try {
      const res = await fetch(`${API_BASE_URL}admin/users/${user.id}/deactivate`, { method: 'POST', credentials: 'include' })
      const data = await res.json().catch(() => ({}))
      if (res.ok) await fetchUser()
      else alert(data?.error || 'Failed to deactivate')
    } finally {
      setActionLoading(null)
    }
  }

  const reactivateUser = async () => {
    if (!user || !isAdmin) return
    setActionLoading('reactivate')
    try {
      const res = await fetch(`${API_BASE_URL}admin/users/${user.id}/reactivate`, { method: 'POST', credentials: 'include' })
      const data = await res.json().catch(() => ({}))
      if (res.ok) await fetchUser()
      else alert(data?.error || 'Failed to reactivate')
    } finally {
      setActionLoading(null)
    }
  }

  const llmLabel = (id: string | undefined) => {
    if (!id) return '—'
    const o = llmOptions.find((x) => x.id === id)
    return o?.label ?? id
  }

  const formatCap = (n: number | null | undefined) =>
    n == null ? 'Unlimited' : n.toLocaleString()

  const saveCreditSettings = async (opts?: { useTierDefaultsOnly?: boolean }) => {
    if (!user) return
    setCreditSaving(true)
    setCreditError(null)
    try {
      const body: Record<string, unknown> = opts?.useTierDefaultsOnly
        ? { use_tier_defaults: true }
        : {
            credit_tier: creditTierSelect,
            monthly_message_limit: creditMsgInput.trim() === '' ? null : creditMsgInput.trim(),
            monthly_cost_usd_limit: creditUsdInput.trim() === '' ? null : creditUsdInput.trim(),
          }
      const res = await fetch(`${API_BASE_URL}admin/user/${user.id}/credit-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setCreditError(typeof data?.error === 'string' ? data.error : 'Failed to save credit settings')
        return
      }
      if (opts?.useTierDefaultsOnly) {
        setCreditMsgInput('')
        setCreditUsdInput('')
      }
      await fetchUser()
    } catch {
      setCreditError('Network error')
    } finally {
      setCreditSaving(false)
    }
  }

  const savePreferredChatLlm = async () => {
    if (!user) return
    setLlmSaving(true)
    setLlmError(null)
    try {
      const res = await fetch(`${API_BASE_URL}admin/user/${user.id}/preferred-chat-llm`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          preferred_chat_llm_provider: llmSelect === '' ? null : llmSelect,
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setLlmError(typeof data?.error === 'string' ? data.error : 'Failed to save')
        return
      }
      await fetchUser()
    } catch {
      setLlmError('Network error')
    } finally {
      setLlmSaving(false)
    }
  }

  const cardStyle: React.CSSProperties = {
    background: 'var(--white)',
    border: '1px solid var(--bd)',
    borderRadius: 12,
    boxShadow: 'var(--sh-sm)',
    padding: 20,
  }

  const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 500, color: 'var(--muted)', marginBottom: 6 }
  const valueStyle: React.CSSProperties = { fontSize: 14, color: 'var(--text)' }
  if (loading) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)' }}>
        Loading user…
      </div>
    )
  }
  if (error || !user) {
    return (
      <div style={{ padding: 24 }}>
        <AdminPanelButton type="button" onClick={onBack}>
          <ArrowLeft size={14} /> Back to users
        </AdminPanelButton>
        <p style={{ marginTop: 16, color: 'var(--error)' }}>{error || 'User not found'}</p>
      </div>
    )
  }

  const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username
  const totalInput = sessionsOverallUsage.totalInput
  const totalOutput = sessionsOverallUsage.totalOutput
  const totalCost = sessionsOverallUsage.totalCost

  return (
    <div style={{ padding: 0 }}>
      <AdminPanelButton type="button" onClick={onBack} style={{ marginBottom: 20 }}>
        <ArrowLeft size={14} /> Back to users
      </AdminPanelButton>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 12,
              background: 'var(--navy)',
              color: 'var(--white)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 18,
              fontWeight: 600,
            }}
          >
            {displayName.substring(0, 2).toUpperCase()}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
              <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 500, color: 'var(--text)', margin: 0 }}>
                {displayName}
              </h2>
              {user.role === 'admin' && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: 'var(--gold-lt)',
                    border: '1px solid var(--gold-bd)',
                    borderRadius: 100,
                    padding: '2px 8px',
                    fontSize: 10,
                    fontWeight: 600,
                    color: 'var(--gold)',
                    letterSpacing: '0.5px',
                  }}
                >
                  <Shield size={10} /> Admin
                </span>
              )}
              {user.isActive === false && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: 'rgba(239,68,68,.12)',
                    border: '1px solid rgba(239,68,68,.25)',
                    borderRadius: 100,
                    padding: '2px 8px',
                    fontSize: 10,
                    fontWeight: 600,
                    color: '#F87171',
                  }}
                >
                  Deactivated
                </span>
              )}
            </div>
            {(user.firstName || user.lastName) && (
              <p style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 4px' }}>Username: {user.username}</p>
            )}
            <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Mail size={12} /> {user.email}
            </p>
            <p style={{ fontSize: 12, color: 'var(--muted)', margin: '8px 0 0', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Calendar size={12} /> Joined {formatISTDate(user.createdAt)}
              {user.lastActive && ` · Last active ${formatISTDate(user.lastActive)}`}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
          <div style={{ minWidth: 80, textAlign: 'center' }}>
            <div style={labelStyle}>Chats</div>
            <div style={{ ...valueStyle, fontWeight: 600 }}>{user.chatCount}</div>
          </div>
          <div style={{ minWidth: 80, textAlign: 'center' }}>
            <div style={labelStyle}>Messages</div>
            <div style={{ ...valueStyle, fontWeight: 600 }}>{user.messageCount}</div>
          </div>
        </div>

        <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--bd)', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {isSuperAdmin && user.email !== SUPER_ADMIN_EMAIL && (
            user.role === 'admin' ? (
              <AdminPanelButton
                type="button"
                onClick={demoteToUser}
                disabled={!!actionLoading}
              >
                <ShieldOff size={14} />
                {actionLoading === 'demote' ? '…' : 'Remove admin'}
              </AdminPanelButton>
            ) : (
              <AdminPanelButton
                type="button"
                onClick={promoteToAdmin}
                disabled={!!actionLoading}
              >
                <Shield size={14} />
                {actionLoading === 'promote' ? '…' : 'Make admin'}
              </AdminPanelButton>
            )
          )}
          <AdminPanelButton type="button" onClick={openBusinessDialog}>
            <Building2 size={14} /> Business info
          </AdminPanelButton>
          {isAdmin && user.email !== SUPER_ADMIN_EMAIL && (
            user.isActive === false ? (
              <AdminPanelButton
                type="button"
                variant="primary"
                onClick={reactivateUser}
                disabled={!!actionLoading}
              >
                <UserReactivate size={14} />
                {actionLoading === 'reactivate' ? '…' : 'Reactivate user'}
              </AdminPanelButton>
            ) : (
              <AdminPanelButton
                type="button"
                variant="danger"
                onClick={deactivateUser}
                disabled={!!actionLoading}
              >
                <UserX size={14} />
                {actionLoading === 'deactivate' ? '…' : 'Deactivate user'}
              </AdminPanelButton>
            )
          )}
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3
          style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: 16,
            fontWeight: 500,
            color: 'var(--text)',
            margin: '0 0 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Cpu size={18} /> Chat model (this user)
        </h3>
        <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
          Override the global default from Configurations for this account only. Clear the override to use the workspace default.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
          <div>
            <div style={labelStyle}>Global default (Configurations)</div>
            <div style={valueStyle}>{llmLabel(user.globalChatLlmProvider)}</div>
          </div>
          <div>
            <div style={labelStyle}>Effective model for chats</div>
            <div style={{ ...valueStyle, fontWeight: 600 }}>{llmLabel(user.effectiveChatLlmProvider)}</div>
          </div>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <div style={{ minWidth: 220 }}>
            <label htmlFor="user-llm-override" style={{ ...labelStyle, display: 'block' }}>
              User override
            </label>
            <select
              id="user-llm-override"
              value={llmSelect}
              onChange={(e) => setLlmSelect(e.target.value)}
              style={{
                width: '100%',
                maxWidth: 320,
                padding: '8px 12px',
                fontSize: 14,
                color: 'var(--text)',
                background: 'var(--white)',
                border: '1px solid var(--bd)',
                borderRadius: 9,
              }}
            >
              <option value="">Use global default</option>
              {llmOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <AdminPanelButton type="button" variant="primary" onClick={savePreferredChatLlm} disabled={llmSaving}>
            {llmSaving ? 'Saving…' : 'Save model'}
          </AdminPanelButton>
        </div>
        {llmError && (
          <p style={{ fontSize: 12, color: 'var(--error)', marginTop: 10, marginBottom: 0 }}>{llmError}</p>
        )}
      </div>

      <div style={{ ...cardStyle, marginBottom: 20 }}>
        <h3
          style={{
            fontFamily: "'Playfair Display',serif",
            fontSize: 16,
            fontWeight: 500,
            color: 'var(--text)',
            margin: '0 0 8px',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Coins size={18} /> Monthly credits (IST)
        </h3>
        <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 16px', lineHeight: 1.5 }}>
          Assign a tier for default caps, or set custom limits for this user only. Leave custom fields empty to use the
          tier defaults from Configurations. Usage resets at midnight IST on the 1st.
        </p>

        {user.credit && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 12,
              marginBottom: 16,
              padding: 12,
              background: 'var(--bg2)',
              borderRadius: 9,
              border: '1px solid var(--bd2)',
            }}
          >
            <div>
              <div style={labelStyle}>Messages this month</div>
              <div style={{ ...valueStyle, fontWeight: 600 }}>
                {(user.credit.monthlyUserMessagesUsed ?? 0).toLocaleString()}
                <span style={{ color: 'var(--muted)', fontWeight: 400 }}>
                  {' '}
                  / {formatCap(user.credit.monthlyMessageLimit)}
                </span>
              </div>
            </div>
            <div>
              <div style={labelStyle}>Spend this month (USD)</div>
              <div style={{ ...valueStyle, fontWeight: 600 }}>
                ${(user.credit.monthlyCostUsd ?? 0).toFixed(4)}
                <span style={{ color: 'var(--muted)', fontWeight: 400 }}>
                  {' '}
                  / {user.credit.monthlyCostUsdLimit == null ? 'Unlimited' : `$${user.credit.monthlyCostUsdLimit}`}
                </span>
              </div>
            </div>
            {user.credit.creditLimitExceeded && (
              <div style={{ gridColumn: '1 / -1' }}>
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#F87171',
                    background: 'rgba(239,68,68,.1)',
                    border: '1px solid rgba(239,68,68,.25)',
                    borderRadius: 100,
                    padding: '4px 10px',
                  }}
                >
                  Over monthly credit limit
                </span>
              </div>
            )}
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gap: 14,
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            marginBottom: 14,
          }}
        >
          <div>
            <label htmlFor="user-credit-tier" style={{ ...labelStyle, display: 'block' }}>
              Tier
            </label>
            <select
              id="user-credit-tier"
              value={creditTierSelect}
              onChange={(e) => setCreditTierSelect(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                color: 'var(--text)',
                background: 'var(--white)',
                border: '1px solid var(--bd)',
                borderRadius: 9,
              }}
            >
              {CREDIT_TIER_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
            {user.credit && (
              <p style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 0' }}>
                Tier defaults: {formatCap(user.credit.tierDefaultMonthlyMessageLimit)} msgs,{' '}
                {user.credit.tierDefaultMonthlyCostUsdLimit == null
                  ? 'unlimited'
                  : `$${user.credit.tierDefaultMonthlyCostUsdLimit}`}{' '}
                / month
              </p>
            )}
          </div>
          <div>
            <label htmlFor="user-credit-msg" style={{ ...labelStyle, display: 'block' }}>
              Custom max messages / month
            </label>
            <input
              id="user-credit-msg"
              type="number"
              min={1}
              step={1}
              placeholder={
                user.credit
                  ? `Tier default (${formatCap(user.credit.tierDefaultMonthlyMessageLimit)})`
                  : 'Empty = tier default'
              }
              value={creditMsgInput}
              onChange={(e) => setCreditMsgInput(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                color: 'var(--text)',
                background: 'var(--white)',
                border: '1px solid var(--bd)',
                borderRadius: 9,
              }}
            />
          </div>
          <div>
            <label htmlFor="user-credit-usd" style={{ ...labelStyle, display: 'block' }}>
              Custom max spend / month (USD)
            </label>
            <input
              id="user-credit-usd"
              type="number"
              min={0}
              step="any"
              placeholder={
                user.credit
                  ? `Tier default (${
                      user.credit.tierDefaultMonthlyCostUsdLimit == null
                        ? 'unlimited'
                        : user.credit.tierDefaultMonthlyCostUsdLimit
                    })`
                  : 'Empty = tier default'
              }
              value={creditUsdInput}
              onChange={(e) => setCreditUsdInput(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                fontSize: 14,
                color: 'var(--text)',
                background: 'var(--white)',
                border: '1px solid var(--bd)',
                borderRadius: 9,
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <AdminPanelButton type="button" variant="primary" onClick={() => saveCreditSettings()} disabled={creditSaving}>
            {creditSaving ? 'Saving…' : 'Save credit settings'}
          </AdminPanelButton>
          <AdminPanelButton
            type="button"
            onClick={() => saveCreditSettings({ useTierDefaultsOnly: true })}
            disabled={creditSaving}
          >
            Use tier defaults only
          </AdminPanelButton>
        </div>
        {creditError && (
          <p style={{ fontSize: 12, color: 'var(--error)', marginTop: 10, marginBottom: 0 }}>{creditError}</p>
        )}
      </div>

      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 16, fontWeight: 500, color: 'var(--text)', margin: 0 }}>
            Sessions and usage
          </h3>
          <AdminPanelButton type="button" onClick={() => openChatHistory()}>
            <MessageSquare size={14} /> View chat history
          </AdminPanelButton>
        </div>
        {sessionsLoading ? (
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>Loading sessions…</p>
        ) : sessions.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--muted)' }}>No chat sessions for this user yet.</p>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 24, marginBottom: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={labelStyle}>Input tokens (assistant)</div>
                <div style={valueStyle}>{totalInput.toLocaleString()}</div>
              </div>
              <div>
                <div style={labelStyle}>Output tokens (assistant)</div>
                <div style={valueStyle}>{totalOutput.toLocaleString()}</div>
              </div>
              <div>
                <div style={labelStyle}>Est. cost (USD)</div>
                <div style={valueStyle}>${totalCost.toFixed(4)}</div>
              </div>
              <div>
                <div style={labelStyle}>Loaded sessions</div>
                <div style={valueStyle}>
                  {sessions.length}
                  {sessionsPagination.total > sessions.length ? ` / ${sessionsPagination.total}` : ''}
                </div>
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--bd)', color: 'var(--muted)', fontWeight: 500 }}>Session</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--bd)', color: 'var(--muted)', fontWeight: 500 }}>Created</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid var(--bd)', color: 'var(--muted)', fontWeight: 500 }}>Msgs</th>
                    <th style={{ textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid var(--bd)', color: 'var(--muted)', fontWeight: 500 }}>Status</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid var(--bd)', color: 'var(--muted)', fontWeight: 500 }}>Input</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid var(--bd)', color: 'var(--muted)', fontWeight: 500 }}>Output</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid var(--bd)', color: 'var(--muted)', fontWeight: 500 }}>Cost</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', borderBottom: '1px solid var(--bd)', color: 'var(--muted)', fontWeight: 500 }} />
                  </tr>
                </thead>
                <tbody>
                  {sessions.map((s) => (
                    <tr key={s.id}>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--bd2)', color: 'var(--text)' }}>{s.name || s.id}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--bd2)', color: 'var(--muted)' }}>{s.created_at ? formatISTDate(s.created_at) : '—'}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--bd2)', textAlign: 'right', color: 'var(--text)' }}>{s.message_count ?? 0}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--bd2)', color: 'var(--muted)' }}>
                        {s.is_deleted === 'true' ? (
                          <span style={{ color: '#c2410c', fontWeight: 500 }}>Deleted</span>
                        ) : (
                          'Active'
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--bd2)', textAlign: 'right', color: 'var(--text)' }}>{(s.total_input_tokens || 0).toLocaleString()}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--bd2)', textAlign: 'right', color: 'var(--text)' }}>{(s.total_output_tokens || 0).toLocaleString()}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--bd2)', textAlign: 'right', color: 'var(--text)' }}>${(s.total_cost || 0).toFixed(4)}</td>
                      <td style={{ padding: '8px 12px', borderBottom: '1px solid var(--bd2)', textAlign: 'right' }}>
                        <AdminPanelButton
                          type="button"
                          size="sm"
                          onClick={() => openSingleSessionMessages(s)}
                        >
                          Messages
                        </AdminPanelButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {sessionsPagination.hasMore && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                <AdminPanelButton
                  type="button"
                  size="sm"
                  onClick={loadMoreSessions}
                  disabled={sessionsLoadingMore}
                >
                  {sessionsLoadingMore ? 'Loading…' : 'Load more sessions'}
                </AdminPanelButton>
              </div>
            )}
          </>
        )}
      </div>

      {chatHistoryOpen && user && (
        <UserSessionsModal
          userId={user.id}
          displayName={displayName}
          username={user.username}
          userEmail={user.email}
          initialSessionId={chatHistoryInitialSessionId}
          onClose={() => {
            setChatHistoryOpen(false)
            setChatHistoryInitialSessionId(null)
          }}
        />
      )}
      {singleSessionModal.open && user && singleSessionModal.id && (
        <UserSessionsModal
          userId={user.id}
          displayName={displayName}
          username={user.username}
          userEmail={user.email}
          initialSessionId={singleSessionModal.id}
          initialSessionName={singleSessionModal.name}
          initialSessionCreatedAt={singleSessionModal.createdAt}
          showSessionsList={false}
          onClose={() =>
            setSingleSessionModal({ open: false, id: null, name: '', createdAt: null })
          }
        />
      )}

      <Dialog open={businessDialogOpen} onOpenChange={(o) => !o && setBusinessDialogOpen(false)}>
        <DialogContent className="dialog-ref sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Business information</DialogTitle>
            <DialogDescription>Edit business details for {displayName}</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'grid', gap: 16, padding: '16px 0' }}>
            {(['name', 'about', 'businessType', 'natureOfBusiness', 'industry', 'subindustry', 'location'] as const).map((key) => (
              <div key={key}>
                <label
                  htmlFor={`b-${key}`}
                  style={{ display: 'block', fontSize: 12, fontWeight: 500, color: 'var(--text)', marginBottom: 6 }}
                >
                  {key === 'name' ? 'Name *' : key}
                </label>
                {key === 'about' ? (
                  <textarea
                    id={`b-${key}`}
                    style={{
                      minHeight: 80,
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 14,
                      color: 'var(--text)',
                      background: 'var(--white)',
                      border: '1px solid var(--bd)',
                      borderRadius: 9,
                      outline: 'none',
                      fontFamily: 'inherit',
                    }}
                    value={businessForm[key] ?? ''}
                    onChange={(e) => setBusinessForm((p) => ({ ...p, [key]: e.target.value }))}
                    placeholder={key === 'name' ? 'Business or company name' : ''}
                  />
                ) : (
                  <input
                    id={`b-${key}`}
                    type="text"
                    value={businessForm[key] ?? ''}
                    onChange={(e) => setBusinessForm((p) => ({ ...p, [key]: e.target.value }))}
                    placeholder={key === 'name' ? 'Business or company name' : ''}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      fontSize: 14,
                      color: 'var(--text)',
                      background: 'var(--white)',
                      border: '1px solid var(--bd)',
                      borderRadius: 9,
                      outline: 'none',
                    }}
                  />
                )}
              </div>
            ))}
          </div>
          <DialogFooter style={{ gap: 10, marginTop: 8 }}>
            <AdminPanelButton type="button" onClick={() => setBusinessDialogOpen(false)}>
              Cancel
            </AdminPanelButton>
            <AdminPanelButton type="button" variant="primary" onClick={saveBusinessInfo} disabled={businessSaving}>
              {businessSaving ? 'Saving…' : 'Save'}
            </AdminPanelButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
